import React, { useRef, useState } from 'react';

import Button from '@mui/material/Button';

import { inputTypeFormatters } from '@/components/DynamicForm/utils/functions';
import { formDataChangesFromSchema } from '@/components/DynamicForm/utils/dataMapper';
import { useAppContext } from '@/context';
import uiKits from '@/components/DynamicForm/FormElements/ui-kits/ui-kits';

import {
	ControllableOptionType,
	DynamicFormConfig,
	DynamicFormEl,
	DynamicFormSchema,
	FormDataType,
	SchemaCall,
	UserSpaceFnPipe,
} from './types/DynamicFormTypes';
import { assocPath, createCallBodyByContentType, cssprefix, omit, path, pathOr, pipe } from './utils/utils';
import { smplTmplt } from './smplTmplt';
import { getFieldErrors, validateInput } from './validations';
import { parsePiped, remapObjectWith } from './utils/payloadParsing';
import InputRow from './InputRow';
import { mapJsonSchema, onSubmit } from './utils/onSubmit';
import StepIndicator from './StepIndicator';

interface ApiCallResult {
	assign: string[];
	result: unknown;
	to?: string;
}

interface ProcessedApiListResponse {
	successes: number;
	intendedCalls: number;
	message: string;
	failedAt?: string;
}

interface GenerateCallsResult {
	errStr: string;
	nextFormDataState: FormDataType;
	nextSchemaState: DynamicFormSchema;
	proceed: boolean;
	success: boolean;
}

export interface DynamicFormProps {
	config?: DynamicFormConfig;
	defaultFormData?: FormDataType;
	defaultSchema?: DynamicFormSchema;
	overrodeSubmitHandler?: (data: FormDataType) => void;
	schemaJSON?: string;
	templatedPaths?: string[][];
}

const FOCUSABLE_CSS_SELECTOR = `button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), *[tabindex], *[contenteditable]`;

function findFirstFocusable(container: HTMLElement): HTMLElement | null {
	return container.querySelector(FOCUSABLE_CSS_SELECTOR);
}

const autoFocusFirstFocusable = (element: HTMLElement | null) => {
	if (element) {
		findFirstFocusable(element)?.focus();
	}
};

const scrollToRefTop = (element: HTMLElement | null) => {
	if (element) {
		element?.scrollIntoView({ behavior: 'smooth' });
	}
};

const createErrMsg = (errList: string[]): string => `
Sorry - there${errList.length > 1 ? ' are issues' : "'s an issue"} preventing progress:

${errList.join('\n')}

Give it a moment, then try proceeding to the next step again.
`;

interface PayloadOutcomes {
	hasPayload: (payload: Record<string, unknown>, filePayload: UserSpaceFnPipe, formData: FormDataType) => unknown;
	hasFilePayload: (payload: Record<string, unknown>, filePayload: UserSpaceFnPipe, formData: FormDataType) => unknown;
	hasNoPayload: () => null;
}

const payloadParseOutcomes: PayloadOutcomes = {
	hasFilePayload: (_, filePayload, formData) => parsePiped(filePayload, formData),
	hasPayload: (payload, _, formData) => remapObjectWith(payload, formData),
	hasNoPayload: () => null,
};

export default function DynamicForm({
	config: formConfig = {},
	defaultFormData = {},
	defaultSchema = [] as DynamicFormSchema,
	schemaJSON = '{}',
	templatedPaths = [],
	overrodeSubmitHandler,
}: Readonly<DynamicFormProps>) {
	const { stylingClasses = 'w-full min-w-5 max-w-10 mx-auto my-0' } = formConfig || {};
	const [loading, setLoading] = useState<boolean>(false);
	const [schema, setSchema] = useState<DynamicFormSchema>(defaultSchema);
	const [formData, setFormData] = useState<FormDataType>({ ...defaultFormData, ...formConfig?.defaultFormData });
	const [currentStep, setCurrentStep] = useState<number>(0);
	const [currentFields, setCurrentFields] = useState<DynamicFormEl[]>(schema?.[currentStep]?.fields ?? []);
	const [formErrors, setFormErrors] = useState<Record<string, string[]>>(
		getFieldErrors(schema?.[currentStep]?.fields ?? [], formData)
	);
	const { config: appConfig } = useAppContext();

	const [interactedFields, setInteractedFields] = useState<Record<string, boolean>>({});
	const [completedTasks, setCompletedTasks] = useState<Record<string, number>>({});

	const topOfFormRef = useRef(null);

	const atLastStep = currentStep + 1 === (schema?.length ?? 0);
	const atFirstStep = currentStep === 0;
	const hasErrors = Object.keys(formErrors).length > 0;
	const {
		stepTitle,
		prevButton: {
			text: prevBtnText = 'Back',
			invalidWhen: prevBtnInvalidWhen = [['always', false]],
			onInvalid: prevBtnOnInvalid = 'enable',
			size: prevBtnSize = 'M',
			variant: prevBtnVariant = 'tertiaryDefault',
		} = {},
		nextButton: {
			text: nextBtnText = 'Next',
			onInvalid: nextBtnOnInvalid = 'disable',
			size: nextBtnSize = 'M',
			variant: nextBtnVariant = 'secondaryDefault',
		} = {},
		submitButton: {
			text: submitBtnText = 'Submit',
			size: submitBtnSize = 'M',
			variant: submitBtnVariant = 'primaryDefault',
		} = {},
	} = schema?.[currentStep] ?? {};

	const getInputKeyValue = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
		formElementType: DynamicFormEl['type'],
		formData: FormDataType,
		controlledOptions?: ControllableOptionType
	) => {
		const { value: targetValue, type, name: targetName } = e.target;
		const name = targetName || controlledOptions?.fieldName || '';
		const value = targetValue || controlledOptions?.value;
		const checked = (e.target as HTMLInputElement).checked;
		const files = (e.target as HTMLInputElement).files;

		let typeFormater;
		if (formElementType && inputTypeFormatters[formElementType]) {
			typeFormater = formElementType;
		} else {
			typeFormater = inputTypeFormatters[type] ? type : 'default';
		}
		return {
			name,
			value: inputTypeFormatters[typeFormater](targetValue ?? value, formData[name], checked, files),
		};
	};

	//must need to pass this function into imagepicker
	const handleChange =
		(
			formElementType: DynamicFormEl['type'],
			formData: FormDataType,
			{
				validations = [],
				reset = [],
				customErrs = [],
			}: Pick<DynamicFormEl, 'validations' | 'reset' | 'customErrs'>
		) =>
		(
			e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
			controlledOptions?: ControllableOptionType
		): void => {
			const { name, value: inputVal } = getInputKeyValue(e, formElementType, formData, controlledOptions);

			const validationRes = validateInput(inputVal, validations, customErrs);
			const nextFormState = Object.assign(omit(reset, formData), { [name]: inputVal });

			setFormData(nextFormState);
			setFormErrors(
				validationRes.length === 0 ? omit([name], formErrors) : { ...formErrors, [name]: validationRes }
			);
		};

	const handleSubmit = async (e: React.FormEvent): Promise<void> => {
		e.preventDefault();
		if (!hasErrors) {
			// We will change it soon when we try to make the Zenoti wrapper optional.
			// Trigger overrideSubmitHandler only in cases where we actually need to override the form behavior.
			if (formConfig?.onSubmit) {
				await onSubmit(schema, formData, { formConfig, appConfig });
			} else if (overrodeSubmitHandler) {
				overrodeSubmitHandler(formData);
			}
		}
	};

	const skipTo = (idx: number): void => {
		setCurrentFields(schema?.[idx]?.fields ?? []);
		setFormErrors(getFieldErrors(schema?.[idx]?.fields ?? [], formData));
		setCurrentStep(idx);
	};

	const generateCalls = async (lookupPath: (string | number)[]): Promise<GenerateCallsResult> => {
		const parsedSchema = JSON.parse(smplTmplt(schemaJSON, formData)) as DynamicFormSchema;

		let apiErrors: string[] = [];
		let proceed = true;
		let nextFormDataState: FormDataType = formData;
		let schemaUpdates: ApiCallResult[] = [];

		async function iterateApiCalls(calls: SchemaCall[]): Promise<ProcessedApiListResponse> {
			let currUrl = '';
			let successes = 0;
			try {
				for (const call of calls) {
					// reparse the call with the latest info from the previous iteration
					const parsedCall = JSON.parse(smplTmplt(JSON.stringify(call), nextFormDataState)) as SchemaCall;
					const {
						url,
						method,
						headers = { 'Content-Type': 'application/json' },
						payload,
						filePayload,
						onError = 'halt',
						adaptResultToSchema = {},
						adaptResultToFormData = {},
					} = parsedCall;
					currUrl = url;

					const remappedPayloadKey = [
						payload ? 'hasPayload' : false,
						filePayload ? 'hasFilePayload' : false,
					].reduce((acc, item) => item || acc, 'hasNoPayload') as keyof PayloadOutcomes;

					const remappedPayload = payloadParseOutcomes[remappedPayloadKey](
						payload ?? {},
						filePayload ?? [],
						Object.assign({}, formData, { schema: mapJsonSchema(schema) })
					);

					const response = await fetch(url, {
						method,
						headers,
						...createCallBodyByContentType(headers?.['Content-Type'], remappedPayload),
					} as RequestInit);

					if (!response.ok) {
						proceed = proceed && onError === 'proceed';
						throw new Error(`API call to ${url} failed with status ${response.status}`);
					}

					// only parse the response when the header specifies JSON
					if (headers?.['Content-Type'] === 'application/json') {
						/* eslint-disable-next-line  @typescript-eslint/no-unsafe-assignment */
						const json = await response.json();

						const formDataUpdateObj = remapObjectWith(adaptResultToFormData, json);
						nextFormDataState = Object.assign(nextFormDataState, formDataUpdateObj);
						schemaUpdates = schemaUpdates.concat(
							Object.entries(remapObjectWith(adaptResultToSchema, json)).map(([key, val]) => ({
								assign: key.split('.'),
								result: val,
							}))
						);
					}
					successes += 1;
				}

				return { successes, intendedCalls: calls.length, message: 'All API calls completed successfully' };
			} catch (error) {
				apiErrors = apiErrors.concat(` - ${(error as Error).message}`);
				return {
					successes,
					intendedCalls: calls.length,
					message: error instanceof Error ? error.message : 'Unknown error occurred',
					failedAt: currUrl,
				};
			}
		}
		/* eslint-disable-next-line  @typescript-eslint/no-unsafe-assignment */
		const result = await iterateApiCalls(pathOr([], lookupPath, parsedSchema) as SchemaCall[]);
		if (result.successes === result.intendedCalls) {
			const nextSchemaState: DynamicFormSchema = pipe(
				(schema: DynamicFormSchema) => schema,
				...templatedPaths.map(
					(templatedPath) => (schema: DynamicFormSchema) =>
						assocPath(templatedPath, path(templatedPath, parsedSchema), schema)
				),
				...schemaUpdates.map(
					({ assign, result }) =>
						(schema: DynamicFormSchema) =>
							assocPath(assign, result, schema)
				)
			)(schema);

			return {
				errStr: '',
				nextFormDataState,
				nextSchemaState,
				proceed,
				success: true,
			};
		}

		return {
			errStr: createErrMsg(apiErrors),
			nextFormDataState: formData,
			nextSchemaState: parsedSchema,
			proceed,
			success: false,
		};
	};

	const midStepCall = async (idx: number): Promise<void> => {
		const { errStr, nextFormDataState, nextSchemaState, proceed, success } = await generateCalls([
			currentStep,
			'onUserInteraction',
			`${idx}`,
			'calls',
		]);

		if (success || (!success && proceed)) {
			const formDataChanges = formDataChangesFromSchema(
				schema?.[currentStep]?.onUserInteraction?.[`${idx}`]?.deriveData ?? [],
				formData
			);
			setFormData(Object.assign(nextFormDataState, formDataChanges));
			setSchema(nextSchemaState);
			setCurrentFields(nextSchemaState[currentStep]?.fields || []);
		} else {
			window.alert(errStr);
		}
	};

	const stepForward = async (): Promise<void> => {
		setLoading(true);
		const nextStep = currentStep + 1;

		const { errStr, nextFormDataState, nextSchemaState, proceed, success } = await generateCalls([
			currentStep,
			'onCompletion',
			'calls',
		]);

		if (success || (!success && proceed)) {
			const formDataChanges = formDataChangesFromSchema(
				schema?.[currentStep]?.onCompletion?.deriveData ?? [],
				formData
			);

			setFormData(Object.assign(nextFormDataState, formDataChanges));
			setSchema(nextSchemaState);

			setCurrentFields(nextSchemaState[nextStep]?.fields || []);
			setFormErrors(getFieldErrors(nextSchemaState[nextStep]?.fields || [], nextFormDataState));
			setCurrentStep(nextStep);
			scrollToRefTop(topOfFormRef.current);
			autoFocusFirstFocusable(topOfFormRef.current);
		} else {
			window.alert(errStr);
		}

		setLoading(false);
	};

	const stepBack = (): void => {
		skipTo(currentStep - 1);
	};

	const rootClasses = `${cssprefix}dynamic-form-layout ${stylingClasses}`.concat(
		formConfig?.stepIndicator?.position ? ` ${cssprefix}${formConfig.stepIndicator.position}` : ''
	);

	const loadingClasses = `${cssprefix}loading`.concat(loading ? '' : ` ${cssprefix}hide-loading`);

	return (
		<div className={rootClasses}>
			<StepIndicator {...(formConfig?.stepIndicator || {})} schema={schema} currentStep={currentStep} />
			{/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
			<form className={`${cssprefix}dynamic-form`} onSubmit={handleSubmit} id="dynamic-form" ref={topOfFormRef}>
				<fieldset className={`${cssprefix}fieldset`}>
					<legend className={`${cssprefix}legend`}>{stepTitle}</legend>
					<div className={`${cssprefix}inputs`}>
						{currentFields.map((field, fieldIndex) => (
							<InputRow
								key={`${field.name}_${fieldIndex}`}
								field={field}
								idx={fieldIndex}
								show={true}
								elLibrary={uiKits[formConfig?.uiKit ?? 'default']}
								interactedFields={interactedFields}
								formData={formData}
								formErrors={formErrors}
								completedTasks={completedTasks}
								updateCompletedTasks={setCompletedTasks}
								updateInterActedFields={setInteractedFields}
								handleChange={handleChange}
								skipTo={skipTo}
								midStepCall={midStepCall}
							/>
						))}
					</div>
				</fieldset>
				<nav className={`${cssprefix}form-nav`}>
					{!atFirstStep && (
						<Button
							className={`${cssprefix}nav-button ${!atFirstStep && cssprefix + prevBtnOnInvalid}`}
							variant={prevBtnVariant}
							onClick={stepBack}
							size={prevBtnSize}
							disabled={
								prevBtnOnInvalid === 'disable' &&
								Boolean(parsePiped(prevBtnInvalidWhen as UserSpaceFnPipe, formData))
							}
						>
							{prevBtnText}
						</Button>
					)}
					{!atLastStep ? (
						<Button
							className={`${cssprefix}nav-button ${
								!hasErrors ? `${cssprefix}enable` : `${cssprefix}${nextBtnOnInvalid}`
							}`}
							onClick={() => void stepForward()}
							disabled={hasErrors}
							size={nextBtnSize}
							variant={nextBtnVariant}
						>
							{nextBtnText}
						</Button>
					) : (
						<Button
							className={`${cssprefix}nav-button`}
							type="submit"
							variant={submitBtnVariant}
							size={submitBtnSize}
							disabled={hasErrors}
						>
							{submitBtnText}
						</Button>
					)}
				</nav>
			</form>
			<div className={loadingClasses}>
				<span>Loading...</span>
			</div>
		</div>
	);
}
